/** "Avisos com o SE_WEB fechado" na tela de preferências (passo 8, PR 2). É por aqui que a pessoa
 *  liga o push neste aparelho. Desenho do Fable em
 *  `docs/superpowers/specs/2026-10-05-notificacoes-email-e-push/desenho-cartao-push.md`, no mesmo
 *  molde do `CartaoSomDoAparelho`. Sem lógica própria além de escolher o estado:
 *  iPhone fora da Tela de Início → sem suporte → bloqueado → erro → em outro app → ligado → desligado.
 *  Sem toast: tudo o que acontece aparece no próprio cartão (região viva `aria-live`).
 *  Ao abrir, confere o push do aparelho (`sincronizarPush`, motivo `preferencias`); o resultado não é lido aqui, o hook relê em
 *  `seweb:push-mudou`. O passo a passo do estado "bloqueado" é o `PassosParaDesbloquear`, o mesmo do `AvisoDoPush`.
 *  D3 (passo 19): `basePath`, `textos` e `className` — o portal usa o mesmo cartão com a API e os
 *  textos dele.
 *  "Em outro app" (decisão do Augusto, 12/10/2026): o navegador tem UMA inscrição para o SE_WEB e o
 *  Portal; com o push do aparelho no outro app, "ligado" e "Desligar" enganariam (os avisos não
 *  chegam aqui, e desligar mataria o push do outro). Aí o cartão diz para onde os avisos vão e
 *  oferece só "Receber aqui". */
import { useEffect, useId } from "react";
import {
  AlertTriangle,
  BellOff,
  BellPlus,
  BellRing,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Share,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PUSH_BASE_INTERNO } from "@/hooks/clienteDoPush";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { sincronizarPush } from "@/lib/push/sincronizarPush";
import { ehIos, estaInstalado } from "@/lib/pwaInstall";
import { cn } from "@/lib/utils";
import {
  SO_SOM_E_AVISO,
  TEXTOS_DO_PUSH_INTERNO,
  type TextosDoPush,
} from "./CartaoPushDoAparelho.textos";
import { PassosParaDesbloquear } from "./PassosParaDesbloquear";

const PALAVRA_DO_PASSO = "font-medium text-foreground";

interface Props {
  /** Qual API de push falar. Sem ele, a interna; o portal passa `PUSH_BASE_PORTAL` (`/portal/push`). */
  basePath?: string;
  /** Os textos (padrão: os do SE_WEB). */
  textos?: TextosDoPush;
  /** A moldura do cartão (o portal usa a dele). */
  className?: string;
}

export function CartaoPushDoAparelho({
  basePath,
  textos = TEXTOS_DO_PUSH_INTERNO,
  className,
}: Props = {}) {
  const {
    isSupported,
    permission,
    subscription,
    isSubscribing,
    isUnsubscribing,
    pushEmOutroApp,
    error,
    subscribe,
    unsubscribe,
  } = usePushSubscription(basePath);
  const tituloId = useId();

  // Ao abrir a tela, confere o push deste aparelho (plano "Push: religar sozinho ou avisar", Decisão 6: Preferências não tem intervalo
  // mínimo, quem abre a tela quer o estado de agora). O resultado não é lido aqui: a sincronização termina em `seweb:push-mudou` e o hook
  // relê a permissão e a inscrição, então os estados do cartão não esperam por ela nem mudam de ordem. `aindaVale` cai ao desmontar, e
  // um "Sair" no meio não deixa dono, intenção nem confirmação gravados sem sessão por trás.
  useEffect(() => {
    let vivo = true;
    void sincronizarPush(basePath ?? PUSH_BASE_INTERNO, "preferencias", { aindaVale: () => vivo });
    return () => {
      vivo = false;
    };
  }, [basePath]);

  // Ordem de decisão fixada na spec (não mude):
  const iosForaDaTela = ehIos() && !estaInstalado();
  const semComoLigar = !iosForaDaTela && (!isSupported || permission === "denied");

  let corpo: JSX.Element;
  let acao: JSX.Element | null = null;

  if (iosForaDaTela) {
    corpo = (
      <>
        <p>{textos.ios.intro}</p>
        <ol className="list-decimal space-y-1 pl-4">
          <li>
            No Safari, toque em{" "}
            <Share aria-hidden="true" className="inline h-3.5 w-3.5 align-text-bottom" />{" "}
            <strong className={PALAVRA_DO_PASSO}>Compartilhar</strong>.
          </li>
          <li>
            Toque em <strong className={PALAVRA_DO_PASSO}>Adicionar à Tela de Início</strong> e
            confirme.
          </li>
          <li>{textos.ios.passoAbrir}</li>
        </ol>
      </>
    );
  } else if (!isSupported) {
    corpo = <p>{textos.semSuporte}</p>;
  } else if (permission === "denied") {
    corpo = (
      <>
        <p>{textos.bloqueado.intro}</p>
        <PassosParaDesbloquear textos={textos} />
      </>
    );
  } else if (error) {
    // `unsubscribe` também grava `error` quando falha, e aí a inscrição continua de pé: o que
    // falhou foi desligar, e "Tentar de novo" tem de tentar desligar (não ligar de novo). Com o push
    // em outro app a inscrição também está de pé, mas não é nossa para desligar: o que falhou foi o
    // "Receber aqui" (único botão desse estado), e tentar de novo é ligar.
    const falhouAoDesligar = Boolean(subscription) && !pushEmOutroApp;
    const tentarDeNovo = falhouAoDesligar ? unsubscribe : subscribe;
    const esperando = falhouAoDesligar ? isUnsubscribing : isSubscribing;
    corpo = (
      <>
        <p className="flex items-start gap-1.5 text-destructive dark:text-red-400">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="break-words">
            {`Não deu para ${falhouAoDesligar ? "desligar" : "ligar"}: ${error}`}
          </span>
        </p>
        <p>{textos.orientacaoDoErro}</p>
      </>
    );
    acao = (
      <Button
        variant="outline"
        size="sm"
        className="min-h-[44px] sm:min-h-0"
        disabled={esperando}
        onClick={() => void tentarDeNovo()}
      >
        {esperando ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="mr-1 h-3 w-3" />
        )}
        Tentar de novo
      </Button>
    );
  } else if (pushEmOutroApp) {
    corpo = <p>{textos.emOutroApp}</p>;
    acao = (
      <Button
        size="sm"
        className="min-h-[44px] sm:min-h-0"
        disabled={isSubscribing}
        onClick={() => void subscribe()}
      >
        {isSubscribing ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <BellPlus className="mr-1 h-3 w-3" />
        )}
        {textos.receberAqui}
      </Button>
    );
  } else if (subscription) {
    corpo = (
      <>
        <p className="flex items-start gap-1.5 text-foreground">
          <CheckCircle2 aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>{textos.ligado}</span>
        </p>
        <p>
          {SO_SOM_E_AVISO} Vale só para este aparelho: em outro celular ou computador, ligue por lá.
        </p>
      </>
    );
    acao = (
      <Button
        variant="outline"
        size="sm"
        className="min-h-[44px] sm:min-h-0"
        disabled={isUnsubscribing}
        onClick={() => void unsubscribe()}
      >
        {isUnsubscribing ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <BellOff className="mr-1 h-3 w-3" />
        )}
        Desligar
      </Button>
    );
  } else {
    corpo = (
      <p>
        {textos.desligado} {SO_SOM_E_AVISO} Ao ligar, o navegador pede sua permissão.
      </p>
    );
    acao = (
      <Button
        size="sm"
        className="min-h-[44px] sm:min-h-0"
        disabled={isSubscribing}
        onClick={() => void subscribe()}
      >
        {isSubscribing ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <BellPlus className="mr-1 h-3 w-3" />
        )}
        Ligar
      </Button>
    );
  }

  return (
    <Card role="group" aria-labelledby={tituloId} className={className}>
      <CardContent className="flex items-start gap-3 p-4 sm:p-6">
        {/* Bolinha: BellRing/primary onde dá para ligar (ou vai dar, no iPhone); BellOff/muted
            onde não dá. */}
        <span
          aria-hidden="true"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            semComoLigar ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
          )}
        >
          {semComoLigar ? <BellOff className="h-4 w-4" /> : <BellRing className="h-4 w-4" />}
        </span>

        <div className="min-w-0 flex-1">
          <h3 id={tituloId} className="text-sm font-medium">
            {textos.titulo}
          </h3>

          {/* Corpo: muda com o estado; a região viva é SEMPRE esta div (nunca remonta). */}
          <div
            aria-live="polite"
            aria-atomic="true"
            className="space-y-2 text-xs text-muted-foreground"
          >
            {corpo}
          </div>

          {/* Fileira de ações: só nos estados erro, em outro app, ligado e desligado. */}
          {acao && <div className="mt-3 flex flex-wrap gap-2">{acao}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
